import type { ClusterFeature, PointFeature } from 'supercluster'
import type { BBox } from 'geojson'
import {
  type DispositionData,
  type DispositionId,
  type AccountStatusData,
  type AccountStatusId,
  isEmpty,
  type LatLng,
  Logger,
  type GeoIdParts,
  type LocationScorecardSuccess,
  type WorkflowStageId,
} from '@terros/common'

const logger = new Logger('location/util')

export function getSectors(center: LatLng, width: LatLng, cellSize: number): GeoIdParts[] {
  const sectors: GeoIdParts[] = []
  logger.verbose('request', center, width)

  const longRange = getSingleRange(center.longitude, width.longitude / 2, cellSize)
  const latRange = getSingleRange(center.latitude, width.latitude / 2, cellSize)

  longRange.forEach((x) => {
    latRange.forEach((y) => {
      sectors.push({
        cellSize,
        longitude: x,
        latitude: y,
      })
    })
  })
  return sectors
}

export function getSingleRange(value: number, radius = 0, cellSize = 1): number[] {
  if (radius < 0) throw new Error('getSingleRange: radius cannot be negative')
  if (cellSize <= 0) throw new Error('getSingleRange: cellSize must be a positive number')
  const range = new Set<number>()
  const factor = 1 / cellSize
  const floating_point_error = 0.00000001
  const minRange = value - radius
  const maxRange = value + radius + floating_point_error
  logger.verbose(`value=${value} radius=${radius} maxRange=${maxRange} cellSize=${cellSize} factor=${factor}`)
  for (let x = minRange; x <= maxRange; x = x + cellSize) {
    logger.verbose(`x=${x} next=${x + cellSize}`)
    range.add(Math.floor(x * factor) / factor)
  }
  // If the radius is smaller than the cellSize, we can miss these two cases.
  // There may be a more elegant solution but I'm not sure what it is.
  range.add(Math.floor(value * factor) / factor)
  range.add(Math.floor((value + radius) * factor) / factor)
  return [...range]
}

export function getScorecardAccountStatusRecord(
  scorecard: LocationScorecardSuccess
): Record<AccountStatusId | WorkflowStageId, number> {
  const { type, users, locationsPitched, locationsWithActivity, ...record } = scorecard
  return record
}

export function buildDispositionMap(dispositions: DispositionData[]): Map<DispositionId, DispositionData> {
  const map = new Map<DispositionId, DispositionData>()
  dispositions.forEach((disposition) => {
    map.set(disposition.dispositionId, disposition)
  })
  return map
}

export function buildAccountStatusMap(accountStatuses: AccountStatusData[]): Map<AccountStatusId, AccountStatusData> {
  const map = new Map<AccountStatusId, AccountStatusData>()
  accountStatuses.forEach((status) => {
    map.set(status.statusId, status)
  })
  return map
}

/**
 * @returns BBox - longitude, latitude of southwesterly point, longitude, latitude of northeasterly point
 * @see https://datatracker.ietf.org/doc/html/rfc7946#section-5
 */
export function getClusterCoordinates(children: (ClusterFeature<any> | PointFeature<any>)[]): BBox {
  if (isEmpty(children)) throw new Error('Children array is empty')

  const coordinates = children.map((child) => child.geometry.coordinates)

  const minLat = Math.min(...coordinates.map((coord) => coord[1]))
  const maxLat = Math.max(...coordinates.map((coord) => coord[1]))
  const minLng = Math.min(...coordinates.map((coord) => coord[0]))
  const maxLng = Math.max(...coordinates.map((coord) => coord[0]))

  return [minLng, minLat, maxLng, maxLat]
}

export function getBBoxCenter(bbox: BBox): LatLng {
  return {
    latitude: (bbox[1] + bbox[3]) / 2,
    longitude: (bbox[0] + bbox[2]) / 2,
  }
}

export function getBBoxDelta(bbox: BBox): { latitudeDelta: number; longitudeDelta: number } {
  return {
    latitudeDelta: Math.abs(bbox[3] - bbox[1]),
    longitudeDelta: Math.abs(bbox[2] - bbox[0]),
  }
}
